import {
  ARENA,
  activeObstacleGeneration,
  buildBlockedGrid,
  cellToWorld,
  worldToCell,
} from '../map/arena.js';
import { segmentClear } from './collision.js';
import type { Vec2 } from '../types.js';

/**
 * Deterministic A* on the arena tile grid. Both the server and the client call
 * this with the same inputs, so predicted and authoritative paths match exactly.
 *
 * feature 5.2 — the blocked grid is cached and rebuilt only when the active
 * obstacle set changes (a boss fight swaps in extra pillars — §D14/D15), keyed
 * off `activeObstacleGeneration()`.
 */
let cachedGrid = buildBlockedGrid();
let cachedGen = activeObstacleGeneration();

function blockedGrid(): boolean[][] {
  const gen = activeObstacleGeneration();
  if (gen !== cachedGen) {
    cachedGrid = buildBlockedGrid();
    cachedGen = gen;
  }
  return cachedGrid;
}

function idx(c: number, r: number): number {
  return r * ARENA.cols + c;
}

function nearestFreeCell(grid: boolean[][], c: number, r: number): { c: number; r: number } {
  if (!grid[r]?.[c]) return { c, r };
  for (let radius = 1; radius < Math.max(ARENA.cols, ARENA.rows); radius++) {
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        const nc = c + dc;
        const nr = r + dr;
        if (nc < 0 || nr < 0 || nc >= ARENA.cols || nr >= ARENA.rows) continue;
        if (!grid[nr]?.[nc]) return { c: nc, r: nr };
      }
    }
  }
  return { c, r };
}

const NEIGHBOURS: [number, number, number][] = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, Math.SQRT2],
  [1, -1, Math.SQRT2],
  [-1, 1, Math.SQRT2],
  [-1, -1, Math.SQRT2],
];

/**
 * Returns a list of world-space waypoints from `start` to `goal` (excluding the
 * start, including the goal), routed around obstacles. Returns `[goal]` when
 * there is direct line of sight, and `[]` when no path exists.
 */
export function findPath(start: Vec2, goal: Vec2): Vec2[] {
  if (segmentClear(start, goal, 4)) return [{ x: goal.x, y: goal.y }];

  const grid = blockedGrid();
  const s = worldToCell(start.x, start.y);
  const g0 = worldToCell(goal.x, goal.y);
  const startCell = nearestFreeCell(grid, s.c, s.r);
  const goalCell = nearestFreeCell(grid, g0.c, g0.r);

  const total = ARENA.cols * ARENA.rows;
  const gScore = new Float64Array(total).fill(Infinity);
  const fScore = new Float64Array(total).fill(Infinity);
  const cameFrom = new Int32Array(total).fill(-1);
  const closed = new Uint8Array(total);

  const startI = idx(startCell.c, startCell.r);
  const goalI = idx(goalCell.c, goalCell.r);
  const h = (c: number, r: number): number => Math.hypot(c - goalCell.c, r - goalCell.r);

  gScore[startI] = 0;
  fScore[startI] = h(startCell.c, startCell.r);

  // Small binary heap keyed by fScore, tie-broken by cell index for determinism.
  const heap: number[] = [startI];
  const less = (a: number, b: number): boolean =>
    fScore[a] !== fScore[b] ? fScore[a]! < fScore[b]! : a < b;
  const push = (v: number): void => {
    heap.push(v);
    let i = heap.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (less(heap[i]!, heap[p]!)) {
        [heap[i], heap[p]] = [heap[p]!, heap[i]!];
        i = p;
      } else break;
    }
  };
  const pop = (): number => {
    const top = heap[0]!;
    const last = heap.pop()!;
    if (heap.length > 0) {
      heap[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        let m = i;
        if (l < heap.length && less(heap[l]!, heap[m]!)) m = l;
        if (r < heap.length && less(heap[r]!, heap[m]!)) m = r;
        if (m === i) break;
        [heap[i], heap[m]] = [heap[m]!, heap[i]!];
        i = m;
      }
    }
    return top;
  };

  while (heap.length > 0) {
    const current = pop();
    if (current === goalI) break;
    if (closed[current]) continue;
    closed[current] = 1;

    const cc = current % ARENA.cols;
    const cr = (current - cc) / ARENA.cols;

    for (const [dc, dr, cost] of NEIGHBOURS) {
      const nc = cc + dc;
      const nr = cr + dr;
      if (nc < 0 || nr < 0 || nc >= ARENA.cols || nr >= ARENA.rows) continue;
      if (grid[nr]?.[nc]) continue;
      // Disallow cutting diagonally through a wall corner.
      if (dc !== 0 && dr !== 0 && (grid[cr]?.[nc] || grid[nr]?.[cc])) continue;

      const ni = idx(nc, nr);
      if (closed[ni]) continue;
      const tentative = gScore[current]! + cost;
      if (tentative < gScore[ni]!) {
        cameFrom[ni] = current;
        gScore[ni] = tentative;
        fScore[ni] = tentative + h(nc, nr);
        push(ni);
      }
    }
  }

  if (cameFrom[goalI] === -1 && startI !== goalI) return [];

  const cells: number[] = [];
  let node = goalI;
  cells.push(node);
  while (node !== startI && cameFrom[node] !== -1) {
    node = cameFrom[node]!;
    cells.push(node);
  }
  cells.reverse();

  const worldPts: Vec2[] = cells.map((ci) => {
    const c = ci % ARENA.cols;
    const r = (ci - c) / ARENA.cols;
    return cellToWorld(c, r);
  });
  worldPts.push({ x: goal.x, y: goal.y });

  return smooth(start, worldPts);
}

/** String-pull: drop waypoints that are visible past. */
function smooth(start: Vec2, pts: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  let anchor = start;
  let i = 0;
  while (i < pts.length) {
    let j = pts.length - 1;
    for (; j > i; j--) {
      if (segmentClear(anchor, pts[j]!, 10)) break;
    }
    out.push(pts[j]!);
    anchor = pts[j]!;
    i = j + 1;
  }
  return out;
}
